import { useAppTranslation } from "@/utils";
import {
  Button,
  Col,
  Drawer,
  Flex,
  Input,
  LegacyCard,
  LegacyForm,
  Page,
  Row,
  Typography,
  useMessagedAsyncFn,
  usePaypingContext,
} from "@payping/web-components";
import { SubLayout } from "@/components/SubLayout";
import { Close, Lock, Warning } from "@payping/icons";
import { DashboardRoutes } from "@/router";
import { Link, useNavigate } from "react-router-dom";
import { SamanApis } from "@payping/api";
import { ValidSheba } from "@payping/utils/validation";
import { backButtonId, closeId, continueId, guideId } from "@/assets/googleTrackIds";

export interface ConnectBankValue {
  iban: string;
  userName: string;
  password: string;
  channelName: string;
  secretKey: string;
  clientId: string;
  clientSecret: string;
}

const GetInfo = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const [form] = LegacyForm.useForm<ConnectBankValue>();

  const [{ loading }, onFinish] = useMessagedAsyncFn(
    async (value: ConnectBankValue) => {
      await SamanApis.connectBankAccountWithRequestModelWithCancellationTokenPost({
        iban: `IR${value.iban}`,
        userName: value.userName,
        password: value.password,
        channelName: value.channelName,
        secretKey: value.secretKey,
        clientId: value.clientId,
        clientSecret: value.clientSecret,
      });
      navigate(DashboardRoutes.digitalAccount.saman.access.success.url);
    },
    [navigate],
    undefined,
    {
      useProgress: true,
    },
  );

  return (
    <>
      <Drawer
        title={<Typography.Text>{t("digitalAccount.termsAndCondition.samanBankAccountOpeningForm")}</Typography.Text>}
        open={true}
        placement={"bottom"}
        closable={false}
        size={"100%"}
        extra={
          <Button
            type={"text"}
            icon={<Close width={14} height={14} />}
            onClick={() => {
              navigate(DashboardRoutes.digitalAccount.home?.url);
            }}
            data-google-track={closeId}
          />
        }
      >
        <Page>
          <SubLayout>
            <div className="tw:flex! tw:justify-center!">
              <Flex vertical gap={32}>
                <div className="tw:flex! tw:justify-center!">
                  <Lock width={84} />
                </div>
                <Typography.Title level={4}>
                  {t("digitalAccount.samanGetInfo.sendUserNameAndPassword")}
                </Typography.Title>
                <LegacyForm<ConnectBankValue> form={form} onFinish={onFinish} layout={"vertical"}>
                  <LegacyForm.Item label={t("shaba")}>
                    <LegacyForm.Item
                      name={"iban"}
                      rules={[
                        { required: true },
                        {
                          validator(_, value) {
                            if (value && !ValidSheba(`IR${value}`)) {
                              return Promise.reject(new Error(t("shabaValidation")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input addonAfter={"IR"} />
                    </LegacyForm.Item>
                  </LegacyForm.Item>
                  <LegacyForm.Item label={t("common.username")} name={"userName"}>
                    <Input />
                  </LegacyForm.Item>
                  <LegacyForm.Item label={t("pass")} name={"password"}>
                    <Input.Password placeholder={"password"} />
                  </LegacyForm.Item>
                  <LegacyForm.Item name={"secretKey"}>
                    <Input.Password placeholder={"secretKey"} />
                  </LegacyForm.Item>
                  <LegacyForm.Item name={"channelName"}>
                    <Input placeholder={"channelName"} />
                  </LegacyForm.Item>
                  <LegacyForm.Item name={"clientId"}>
                    <Input placeholder={"clientId"} />
                  </LegacyForm.Item>
                  <LegacyForm.Item name={"clientSecret"}>
                    <Input.Password placeholder={"clientSecret"} />
                  </LegacyForm.Item>
                  <LegacyForm.Item>
                    <Flex vertical={!md} className={"tw:justify-between md:tw:flex!"} gap={16}>
                      <Typography.Text className="tw:flex! tw:items-center">
                        {t("digitalAccount.samanGetInfo.don'tHaveTheUsernameAndPasswordAndOtherInformation")}
                      </Typography.Text>
                      <Link to={DashboardRoutes.digitalAccount.saman.access.help?.url} data-google-track={guideId}>
                        <Button className={"tw:w-fit"} type={"default"}>
                          {t("digitalAccount.samanGetInfo.receiveGuide")}
                        </Button>
                      </Link>
                    </Flex>
                  </LegacyForm.Item>
                  <LegacyForm.Item>
                    <LegacyCard.Ant>
                      <Flex gap={16}>
                        <div className="tw:flex! tw:items-start">
                          <Warning className="color--warning" width={16} />
                        </div>
                        <Typography.Text className={"tw:whitespace-pre-line"}>
                          {t("digitalAccount.samanGetInfo.getInfoWarning")}
                        </Typography.Text>
                      </Flex>
                    </LegacyCard.Ant>
                  </LegacyForm.Item>
                  {md ? (
                    <Flex className="tw:flex! tw:justify-center!" gap={8}>
                      <Button type={"primary"} loading={loading} htmlType={"submit"} data-google-track={continueId}>
                        {t("digitalAccount.samanGetInfo.connectAndFinish")}
                      </Button>
                      <Link
                        to={DashboardRoutes.digitalAccount.saman.getAccessAccount.url}
                        data-google-track={backButtonId}
                      >
                        <Button type={"default"}>{t("back")}</Button>
                      </Link>
                    </Flex>
                  ) : null}
                </LegacyForm>
              </Flex>
            </div>
          </SubLayout>
        </Page>
      </Drawer>
      {!md ? (
        <Drawer open={true} placement={"bottom"} closable={false} mask={false} size={80}>
          <Row gutter={16}>
            <Col flex={3}>
              <Button block type={"primary"} onClick={() => form.submit()} data-google-track={continueId}>
                {t("digitalAccount.samanGetInfo.connectAndFinish")}
              </Button>
            </Col>
            <Col flex={1}>
              <Link to={DashboardRoutes.digitalAccount.saman.getAccessAccount.url} data-google-track={backButtonId}>
                <Button block type={"default"}>
                  {t("back")}
                </Button>
              </Link>
            </Col>
          </Row>
        </Drawer>
      ) : null}
    </>
  );
};

export default GetInfo;
